import { List as FluentUIList } from "@fluentui/react";
import React, { forwardRef } from "react";
import { useComponentLogger } from "../../../shared/logging";
import { BaseProps } from "../../../shared/props";
import { isRTL } from "../../../styles";
import { FocusZone } from "../../FocusZone/FocusZone";
import { ListWrapper } from "../ListWrapper/ListWrapper";
import { ListItemStyleProps, ListWrapperCollapseProps } from "../shared/props";
import { childrenAsElementArray, listItemDefaultBackgroundColor, onRenderCell } from "../shared/utils";

export type ListProps = ListInternalProps & ListWrapperCollapseProps;

export interface ListInternalProps extends BaseProps, ListItemStyleProps { }

export interface ListRef {
    readonly focus: () => void;
}

export const List: React.FC<ListProps & React.RefAttributes<ListRef>> = forwardRef<ListRef, ListProps>((props, ref) => {
    useComponentLogger("List", props.id);

    return (
        <ListWrapper {...props} listType="List" ref={ref} />
    );
});

export const ListInternal: React.FC<ListInternalProps & React.RefAttributes<ListRef>> = forwardRef<ListRef, ListInternalProps>(({
    children,
    listItemBackgroundColor = listItemDefaultBackgroundColor,
    enableVirtualization = false,
    disabled,
    showDivider = true,
    isTabbable = false,
    listRole = "list",
    ...otherProps
}, ref) => {
    const fluentOnRenderCell = (listItem?: JSX.Element) => { // this is to account for when the showDivider is not included in the top level props. I.E listItem is a function.
        return onRenderCell(listItem!, listItemBackgroundColor, disabled, undefined, listItem?.props.showDivider !== undefined ? listItem?.props.showDivider : showDivider);
    };

    const fluentOnShouldVirtualize = () => enableVirtualization;
    const rtl = isRTL();
    const enterOnArrowKey = (ev: React.KeyboardEvent<HTMLElement>): boolean => ev.key === (rtl ? "ArrowLeft" : "ArrowRight");
    const handleTabKey = isTabbable ? "all" : undefined;

    return (
        <FocusZone handleTabKey={handleTabKey} shouldEnterInnerZone={enterOnArrowKey} direction="vertical" ref={ref}>
            <FluentUIList
                {...otherProps}
                items={childrenAsElementArray(children).slice()}
                onRenderCell={fluentOnRenderCell}
                onShouldVirtualize={fluentOnShouldVirtualize}
                role={listRole}
            />
        </FocusZone>
    );
});
